import { UserIcon, MinusIcon } from "@heroicons/react/24/solid";
import { useMemo } from "react";
import { useUserLookup } from "../../hooks/useUserLookup";

type AssigneePillProps = {
  assigneeId: string | null | undefined;
};

export default function AssigneePill({ assigneeId }: AssigneePillProps) {
  const { findUser, isLoading } = useUserLookup();

  const user = useMemo(() => {
    if (!assigneeId) return undefined;
    return findUser(assigneeId);
  }, [assigneeId, findUser]);

  if (!assigneeId) {
    return <UnassignedPill />;
  }

  if (isLoading) {
    return <LoadingPill />;
  }

  if (!user) {
    return <UnassignedPill label="Unknown user" />;
  }

  const displayName =
    user.fullname?.trim() || `${user.forename ?? ""} ${user.surname ?? ""}`.trim();

  const initials = getInitials(displayName);

  return (
    <div className="inline-flex max-w-full items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1.5 shadow-sm">
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[11px] font-bold text-blue-700 ring-1 ring-inset ring-blue-100">
        {initials || <UserIcon className="h-3.5 w-3.5" />}
      </span>

      <span className="truncate text-sm font-medium text-slate-700">
        {displayName}
      </span>
    </div>
  );
}

function UnassignedPill({ label = "Unassigned" }: { label?: string }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-dashed border-slate-200 bg-slate-50 px-2.5 py-1.5">
      <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200/70 text-slate-500">
        <MinusIcon className="h-3.5 w-3.5" />
      </span>

      <span className="text-sm font-medium text-slate-500">{label}</span>
    </div>
  );
}

function LoadingPill() {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1.5 shadow-sm">
      <span className="h-6 w-6 shrink-0 animate-pulse rounded-full bg-slate-200" />
      <span className="h-4 w-20 animate-pulse rounded bg-slate-200" />
    </div>
  );
}

function getInitials(fullname: string) {
  return fullname
    .trim()
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}